import React, { useState, useEffect } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { voidPayment } from "../services/paymentService.js";
import { PaymentTallyModal } from "./PaymentTallyModal.jsx";
import { VendorPaymentModal } from "../../purchases/components/VendorPaymentModal.jsx";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";

/** Confirmation sheet to void a payment, with a required reason and a summary of what is reversed. */
export function VoidPaymentSheet({ isOpen, onClose, payment, onVoided }) {
  const { isProtected, verify, refresh } = useDangerConfirm();
  const [deletePassword, setDeletePassword] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setDeletePassword("");
      setError("");
      refresh(); // pick up protection turned on/off by another user
    }
  }, [isOpen, payment?.id]);

  if (!payment) return null;

  const isIn = payment.type !== "PAYMENT_OUT";
  const allocations = (payment.allocations || []).filter((a) => Number(a.allocatedAmount || 0) > 0);
  const partyName = payment.partyName || payment.customerName || payment.vendorName || "the party";

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError("Please enter a reason.");
      return;
    }
    if (isProtected && !deletePassword) {
      setError("Enter the delete password.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      if (isProtected && !(await verify(deletePassword))) {
        setError("Wrong delete password. The payment was not voided.");
        setDeletePassword("");
        return;
      }
      await voidPayment(payment.id, reason);
      if (onVoided) await onVoided(payment);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to void payment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={`🚫 Void ${payment.paymentNo || "Payment"}`}>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
        <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
          This cancels the {isIn ? "receipt" : "payment"} of{" "}
          <strong style={{ color: "var(--text-main)" }}>₹{Number(payment.amount || 0).toLocaleString("en-IN")}</strong>{" "}
          {isIn ? "from" : "to"} <strong style={{ color: "var(--text-main)" }}>{partyName}</strong>. It stays in history marked VOID.
        </div>

        {allocations.length > 0 && (
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.65rem 0.85rem" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "0.35rem" }}>
              Amount put back as due
            </div>
            {allocations.map((a, idx) => (
              <div key={idx} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", padding: "2px 0" }}>
                <span>{a.invoiceNo || a.billNo || "Bill"}</span>
                <strong>₹{Number(a.allocatedAmount || 0).toLocaleString("en-IN")}</strong>
              </div>
            ))}
          </div>
        )}

        <div>
          <label className="form-label">Reason <span style={{ color: "var(--danger)" }}>*</span></label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Entered for wrong customer / wrong amount"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
        </div>

        {isProtected && (
          <div>
            <label className="form-label">🔒 Delete password <span style={{ color: "var(--danger)" }}>*</span></label>
            <input
              type="password"
              className="form-control"
              autoComplete="off"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
            />
          </div>
        )}

        {error && (
          <div style={{ background: "rgba(239, 68, 68, 0.12)", border: "1px solid rgba(239, 68, 68, 0.35)", padding: "0.6rem 0.85rem", borderRadius: "var(--radius-md)", color: "#fca5a5", fontSize: "0.85rem" }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.75rem" }}>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting} style={{ flex: 1 }}>
            Keep Payment
          </Button>
          <Button type="button" variant="danger" onClick={handleConfirm} loading={submitting} style={{ flex: 1 }}>
            Void Payment
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

/**
 * Hosts the edit and void flows for a payment record.
 * Pass `editPayment` to open the matching edit modal, or `voidingPayment` to open the void sheet.
 */
export function PaymentCorrectionHost({ editPayment, voidingPayment, onCloseEdit, onCloseVoid, onChanged }) {
  const isVendorEdit = editPayment?.type === "PAYMENT_OUT";

  return (
    <>
      <PaymentTallyModal
        isOpen={!!editPayment && !isVendorEdit}
        onClose={onCloseEdit}
        editingPayment={!isVendorEdit ? editPayment : null}
        onPaymentSaved={onChanged}
      />
      <VendorPaymentModal
        isOpen={!!editPayment && isVendorEdit}
        onClose={onCloseEdit}
        editingPayment={isVendorEdit ? editPayment : null}
        onPaymentSaved={onChanged}
      />
      <VoidPaymentSheet
        isOpen={!!voidingPayment}
        onClose={onCloseVoid}
        payment={voidingPayment}
        onVoided={onChanged}
      />
    </>
  );
}

export default PaymentCorrectionHost;
